import React, { useState, useCallback } from 'react';
import { 
  View, 
  Text, 
  ScrollView, 
  TouchableOpacity, 
  Dimensions,
  Alert,
  Platform
} from 'react-native';
import { 
  XStack, 
  YStack, 
  Avatar
} from 'tamagui';
import { 
  MaterialIcons
} from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';

interface Task {
  _id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in-progress' | 'review' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string[];
  dueDate?: string;
  createdAt: string;
  updatedAt: string;
  workPackageId: string;
  tags?: string[];
  attachments?: any[];
  comments?: any[];
  timeTracking?: {
    estimated: number;
    actual: number;
  };
}

interface ModernKanbanBoardProps {
  tasks: {
    'todo': Task[];
    'in-progress': Task[];
    'review': Task[];
    'done': Task[];
  };
  onTaskEdit: (task: Task) => void;
  onTaskCreate: () => void;
  onTaskDragEnd: (result: any) => void;
  selectedTasks: string[];
  onTaskSelect: (taskIds: string[]) => void;
  viewMode: 'kanban' | 'list' | 'board';
  onViewModeChange: (mode: 'kanban' | 'list' | 'board') => void;
}

const ModernKanbanBoard: React.FC<ModernKanbanBoardProps> = ({
  tasks,
  onTaskEdit,
  onTaskCreate,
  onTaskDragEnd,
  selectedTasks,
  onTaskSelect,
  viewMode,
  onViewModeChange
}) => {
  const theme = themeSelector();
  const { width: screenWidth } = Dimensions.get('window');
  const isTablet = screenWidth > 768;
  
  console.log('🎯 ModernKanbanBoard received tasks:', {
    tasksObject: tasks,
    taskCounts: {
      todo: tasks?.todo?.length || 0,
      'in-progress': tasks?.['in-progress']?.length || 0,
      review: tasks?.review?.length || 0,
      done: tasks?.done?.length || 0
    },
    totalTasks: Object.values(tasks || {}).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0),
    viewMode,
    sampleTasks: {
      todo: tasks?.todo?.slice(0, 2) || [],
      'in-progress': tasks?.['in-progress']?.slice(0, 2) || [],
      review: tasks?.review?.slice(0, 2) || [],
      done: tasks?.done?.slice(0, 2) || []
    }
  });

  const statusConfig = {
    'todo': {
      title: 'To Do',
      color: '#6b7280',
      icon: 'radio-button-unchecked',
      backgroundColor: theme ? '#f9fafb' : '#374151'
    },
    'in-progress': {
      title: 'In Progress',
      color: '#3b82f6',
      icon: 'play-circle-outline',
      backgroundColor: theme ? '#eff6ff' : '#1e3a8a'
    },
    'review': {
      title: 'Review',
      color: '#f59e0b',
      icon: 'rate-review',
      backgroundColor: theme ? '#fef3c7' : '#92400e'
    },
    'done': {
      title: 'Done',
      color: '#10b981',
      icon: 'check-circle',
      backgroundColor: theme ? '#d1fae5' : '#065f46'
    }
  };

  const [selectedTaskForMove, setSelectedTaskForMove] = useState<Task | null>(null);
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const isTaskSelected = (taskId: string) => selectedTasks.includes(taskId);

  const toggleTaskSelection = (taskId: string) => {
    const newSelection = isTaskSelected(taskId)
      ? selectedTasks.filter(id => id !== taskId)
      : [...selectedTasks, taskId];
    onTaskSelect(newSelection);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#eab308';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const handleTaskLongPress = (task: Task) => {
    setSelectedTaskForMove(task);
    Alert.alert(
      'Move Task',
      `Move "${task.title}" to which column?`,
      [
        { text: 'Cancel', style: 'cancel', onPress: () => setSelectedTaskForMove(null) },
        { text: 'To Do', onPress: () => moveTaskToStatus(task, 'todo') },
        { text: 'In Progress', onPress: () => moveTaskToStatus(task, 'in-progress') },
        { text: 'Review', onPress: () => moveTaskToStatus(task, 'review') },
        { text: 'Done', onPress: () => moveTaskToStatus(task, 'done') },
      ]
    );
  };

  const moveTaskToStatus = (task: Task, newStatus: string) => {
    const currentStatus = task.status;

    if (currentStatus === newStatus) {
      setSelectedTaskForMove(null);
      return;
    }

    const currentTasks = tasks[currentStatus] || [];
    const currentIndex = currentTasks.findIndex(t => t._id === task._id);

    if (currentIndex === -1) {
      console.warn('Task not found in current status column');
      setSelectedTaskForMove(null);
      return;
    }

    onTaskDragEnd({
      source: { droppableId: currentStatus, index: currentIndex },
      destination: { droppableId: newStatus, index: 0 },
      draggableId: task._id
    });

    setSelectedTaskForMove(null);
  };

  // Web-only drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, task: Task) => {
    if (Platform.OS !== 'web') return;

    console.log('🎯 Drag started for task:', task._id);
    setDraggedTask(task);

    // Set drag data
    e.dataTransfer.setData('application/json', JSON.stringify({
      taskId: task._id,
      sourceStatus: task.status
    }));

    // Set drag effect
    e.dataTransfer.effectAllowed = 'move';

    // Add visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
      e.currentTarget.style.transform = 'scale(1.05)';
    }
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    if (Platform.OS !== 'web') return;

    console.log('🎯 Drag ended');
    setDraggedTask(null);
    setDragOverColumn(null);

    // Reset visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
      e.currentTarget.style.transform = 'scale(1)';
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, status: string) => {
    if (Platform.OS !== 'web') return;

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(status);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (Platform.OS !== 'web') return;

    // Only clear if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverColumn(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetStatus: string) => {
    if (Platform.OS !== 'web') return;

    e.preventDefault();
    setDragOverColumn(null);

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      const { taskId, sourceStatus } = dragData;

      if (sourceStatus === targetStatus) {
        console.log('❌ Same status, no change needed');
        return;
      }

      console.log('✅ Dropping task:', taskId, 'from', sourceStatus, 'to', targetStatus);

      // Find the task in the current tasks
      const currentTasks = tasks[sourceStatus as keyof typeof tasks] || [];
      const taskIndex = currentTasks.findIndex(t => t._id === taskId);

      if (taskIndex === -1) {
        console.error('❌ Task not found in source column');
        return;
      }

      // Call the parent's drag end handler with the same format as react-beautiful-dnd
      onTaskDragEnd({
        source: { droppableId: sourceStatus, index: taskIndex },
        destination: { droppableId: targetStatus, index: 0 },
        draggableId: taskId
      });

    } catch (error) {
      console.error('❌ Error parsing drag data:', error);
    }
  }, [tasks, onTaskDragEnd]);

  const renderTask = (task: Task, index: number) => {
    const selected = isTaskSelected(task._id);
    const isDragging = draggedTask?._id === task._id;
    
    console.log('🎯 Rendering task for drag:', {
      taskId: task._id,
      taskTitle: task.title,
      index,
      selected,
      hasValidId: !!task._id,
      isDragging
    });

    // Validate task has required fields
    if (!task._id) {
      console.error('❌ Task missing _id, cannot render for drag:', task);
      return null;
    }

    return (
      <div
        key={task._id}
        draggable={Platform.OS === 'web'}
        onDragStart={(e) => handleDragStart(e, task)}
        onDragEnd={handleDragEnd}
        style={{
          marginBottom: 12,
          backgroundColor: selected
            ? (theme ? '#dbeafe' : '#1e40af')
            : isDragging
              ? (theme ? '#dbeafe' : '#1e3a8a')
              : (theme ? '#ffffff' : '#1f2937'),
          borderRadius: 12,
          padding: 16,
          borderWidth: selected ? 2 : 1,
          borderColor: selected
            ? '#3b82f6'
            : isDragging
              ? '#3b82f6'
              : (theme ? '#e5e7eb' : '#374151'),
          borderStyle: 'solid',
          boxShadow: isDragging
            ? '0 20px 40px rgba(59, 130, 246, 0.6)'
            : selected
              ? '0 4px 12px rgba(59, 130, 246, 0.2)'
              : '0 2px 4px rgba(0,0,0,0.1)',
          transform: isDragging ? 'scale(1.05) rotate(1deg)' : 'scale(1)',
          opacity: isDragging ? 0.5 : 1,
          cursor: Platform.OS === 'web' ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
          transition: isDragging ? 'none' : 'all 0.2s ease',
          zIndex: isDragging ? 9999 : 1,
          position: 'relative',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
        onClick={() => !isDragging && onTaskEdit(task)}
        {...(Platform.OS !== 'web' && { onLongPress: () => handleTaskLongPress(task) })}
      >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div style={{ flex: 1, marginRight: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{
                    padding: 4,
                    borderRadius: 4,
                    backgroundColor: theme ? '#f3f4f6' : '#374151',
                    cursor: 'grab',
                  }}>
                    <MaterialIcons
                      name="drag-indicator"
                      size={16}
                      color={theme ? '#6b7280' : '#9ca3af'}
                    />
                  </div>
                  <h3 style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? '#111827' : '#ffffff',
                    lineHeight: '22px',
                    flex: 1,
                    margin: 0,
                  }}>
                    {task.title}
                  </h3>
                </div>
                
                {task.description && (
                  <p style={{
                    fontSize: 14,
                    color: theme ? '#6b7280' : '#9ca3af',
                    lineHeight: '20px',
                    margin: '8px 0',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical' as any,
                  }}>
                    {task.description}
                  </p>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    paddingLeft: 8,
                    paddingRight: 8,
                    paddingTop: 4,
                    paddingBottom: 4,
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: '500',
                    backgroundColor: task.priority === 'urgent' 
                      ? '#fef2f2' : task.priority === 'high' 
                      ? '#fff7ed' : task.priority === 'medium' 
                      ? '#fefce8' : '#f0fdf4',
                    color: task.priority === 'urgent' 
                      ? '#dc2626' : task.priority === 'high' 
                      ? '#ea580c' : task.priority === 'medium' 
                      ? '#ca8a04' : '#16a34a',
                  }}>
                    {task.priority}
                  </span>
                  
                  {task.dueDate && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      color: theme ? '#6b7280' : '#9ca3af',
                    }}>
                      <MaterialIcons name="schedule" size={14} />
                      {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {task.assignedTo && task.assignedTo.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8 }}>
                    {task.assignedTo.slice(0, 3).map((userId, index) => (
                      <div
                        key={userId}
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: '600',
                          color: '#ffffff',
                          marginLeft: index > 0 ? -8 : 0,
                          border: '2px solid ' + (theme ? '#ffffff' : '#1f2937'),
                        }}
                      >
                        {/* {userId?.charAt(0)?.toUpperCase()} */}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
      </div>
    );
  };

  const renderColumn = (status: keyof typeof tasks, columnTasks: Task[]) => {
    const config = statusConfig[status];
    const columnWidth = isTablet ? screenWidth / 4 - 20 : screenWidth - 40;

    return (
      <View
        key={status}
        style={{
          width: columnWidth,
          marginRight: isTablet ? 16 : 0,
          marginBottom: isTablet ? 0 : 20,
        }}
      >
        <XStack 
          justifyContent="space-between" 
          alignItems="center" 
          marginBottom="$3"
          paddingHorizontal="$2"
        >
          <XStack alignItems="center" gap="$2">
            <MaterialIcons 
              name={config.icon as any} 
              size={20} 
              color={config.color} 
            />
            <Text style={{
              fontSize: 16,
              fontWeight: '600',
              color: theme ? '#111827' : '#ffffff',
            }}>
              {config.title}
            </Text>
            <View style={{
              backgroundColor: config.color,
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 12,
            }}>
              <Text style={{
                color: '#ffffff',
                fontSize: 12,
                fontWeight: '600',
              }}>
                {columnTasks.length}
              </Text>
            </View>
          </XStack>

          <TouchableOpacity
            onPress={onTaskCreate}
            style={{
              padding: 4,
              borderRadius: 6,
              backgroundColor: theme ? '#f3f4f6' : '#374151',
            }}
          >
            <MaterialIcons 
              name="add" 
              size={16} 
              color={theme ? '#111827' : '#ffffff'} 
            />
          </TouchableOpacity>
        </XStack>

        <div
          onDragOver={(e) => handleDragOver(e, status)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, status)}
          style={{
            backgroundColor: dragOverColumn === status
              ? (theme ? '#dbeafe' : '#1e3a8a')
              : config.backgroundColor,
            borderRadius: 12,
            padding: 12,
            minHeight: 200,
            borderWidth: dragOverColumn === status ? 3 : 1,
            borderColor: dragOverColumn === status
              ? '#3b82f6'
              : (theme ? '#e5e7eb' : '#374151'),
            borderStyle: dragOverColumn === status ? 'dashed' : 'solid',
            transition: 'all 0.3s ease',
            transform: dragOverColumn === status ? 'scale(1.03)' : 'scale(1)',
            boxShadow: dragOverColumn === status
              ? '0 8px 25px rgba(59, 130, 246, 0.3)'
              : '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
              {columnTasks.length > 0 ? (
                  columnTasks.map((task, index) => {
                    console.log(`🎯 Rendering task ${index} in ${status}:`, {
                      taskId: task._id,
                      taskTitle: task.title,
                      hasValidId: !!task._id,
                      task: task
                    });

                    // Validate task has required fields for drag and drop
                    if (!task._id) {
                      console.error(`❌ Task missing _id:`, task);
                      return null;
                    }

                    return renderTask(task, index);
                  })
              ) : (
                <div
                  style={{
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                        paddingTop: 40,
                        paddingBottom: 40,
                    minHeight: 160,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <MaterialIcons 
                    name="inbox" 
                    size={32} 
                    color={theme ? '#9ca3af' : '#6b7280'} 
                  />
                  <div style={{
                    marginTop: 8,
                    fontSize: 14,
                    color: theme ? '#9ca3af' : '#6b7280',
                    textAlign: 'center',
                  }}>
                    No tasks in {config.title.toLowerCase()}
                  </div>
                </div>
          )}
        </div>
      </View>
    );
  };

  // Debug: Log the tasks structure
  console.log('🎯 ModernKanbanBoard: Final tasks structure:', {
    tasks,
    hasTasks: !!tasks,
    taskKeys: Object.keys(tasks || {}),
    totalTasks: Object.values(tasks || {}).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)
  });

  // Debug: Check if we have valid tasks for drag and drop
  const hasValidTasks = tasks && Object.values(tasks).some(arr => Array.isArray(arr) && arr.length > 0);
  console.log('🎯 ModernKanbanBoard: Has valid tasks for drag and drop:', hasValidTasks);

  // If no valid tasks, show a message
  if (!hasValidTasks) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <Text style={{ fontSize: 16, color: theme ? '#111827' : '#ffffff', textAlign: 'center' }}>
          No tasks available for drag and drop
        </Text>
        <Text style={{ fontSize: 14, color: theme ? '#6b7280' : '#9ca3af', textAlign: 'center', marginTop: 8 }}>
          Create some tasks to test the drag and drop functionality
        </Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: theme ? '#f9fafb' : '#111827'}}>
      <View style={{
        flexDirection: 'row',
        gap: 8,
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: theme ? '#e5e7eb' : '#374151',
      }}>
        {[
          { key: 'kanban', icon: 'view-column', label: 'Kanban' },
          { key: 'list', icon: 'view-list', label: 'List' },
          { key: 'board', icon: 'dashboard', label: 'Board' },
        ].map((mode) => (
          <TouchableOpacity
            key={mode.key}
            onPress={() => onViewModeChange(mode.key as any)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderRadius: 8,
              backgroundColor: viewMode === mode.key 
                ? '#3b82f6' 
                : theme ? '#f3f4f6' : '#374151',
            }}
          >
            <MaterialIcons 
              name={mode.icon as any} 
              size={16} 
              color={viewMode === mode.key ? '#ffffff' : theme ? '#111827' : '#ffffff'} 
            />
            <Text style={{
              fontSize: 14,
              fontWeight: '500',
              color: viewMode === mode.key ? '#ffffff' : theme ? '#111827' : '#ffffff',
            }}>
              {mode.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <ScrollView
        horizontal={isTablet}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          padding: 20,
          flexDirection: isTablet ? 'row' : 'column',
        }}
      >
        {Object.entries(tasks).map(([status, columnTasks]) => 
          renderColumn(status as keyof typeof tasks, columnTasks)
        )}
      </ScrollView>
    </View>
  );
};

export default ModernKanbanBoard;
